/**
 * PlanningRelationshipPack — 5 categories, 14 subtypes covering Gantt
 * dependencies, section containment, scorecard evaluation, fishbone
 * causation, and cross-framework links between planning artifacts.
 *
 * Styling defaults are filled as Epic SP-A / SP-B / SP-C author the
 * real renderers. The structure is stable and downstream epics should
 * only need to change `defaultStyle` entries.
 *
 * See plans/strategy-planning/foundation-scaffold.md § Layer 3.
 */

import type {
  RelationshipCategoryManifest,
  RelationshipPack,
  RelationshipSubtypeManifest,
} from '../../../lib/diagram/relationship-manifest.types';

export type PlanningCategoryId =
  | 'dependency'
  | 'containment'
  | 'evaluation'
  | 'causal'
  | 'cross_framework';

export type PlanningCategory = RelationshipCategoryManifest & {
  id: PlanningCategoryId;
};

export type PlanningSubtype = RelationshipSubtypeManifest & {
  categoryId: PlanningCategoryId;
  appliesTo: string[];
};

export const PlanningRelationshipPack: RelationshipPack = {
  meta: {
    id: 'planning-relationships',
    name: 'Strategy & Planning Relationships',
    version: '1.0.0',
  },
  diagramType: 'planning',

  categories: [
    { id: 'dependency', label: 'Dependencies', order: 1 },
    { id: 'containment', label: 'Containment', order: 2 },
    { id: 'evaluation', label: 'Evaluation & Scoring', order: 3 },
    { id: 'causal', label: 'Causal & Root Cause', order: 4 },
    { id: 'cross_framework', label: 'Cross-Framework', order: 5 },
  ],

  subtypes: [
    // Dependency (Gantt, roadmaps)
    {
      id: 'finish_to_start',
      categoryId: 'dependency',
      label: 'Finish → Start',
      appliesTo: ['gantt'],
      ontology: {
        predicate: 'depends_on',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { dependencyType: 'finish_to_start' },
      },
      defaultStyle: { arrowhead: 'triangle' },
    },
    {
      id: 'start_to_start',
      categoryId: 'dependency',
      label: 'Start → Start',
      appliesTo: ['gantt'],
      ontology: {
        predicate: 'depends_on',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { dependencyType: 'start_to_start' },
      },
      defaultStyle: { arrowhead: 'triangle', strokeDasharray: '4 2' },
    },
    {
      id: 'finish_to_finish',
      categoryId: 'dependency',
      label: 'Finish → Finish',
      appliesTo: ['gantt'],
      ontology: {
        predicate: 'depends_on',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { dependencyType: 'finish_to_finish' },
      },
      defaultStyle: { arrowhead: 'triangle', strokeDasharray: '4 2' },
    },
    {
      id: 'start_to_finish',
      categoryId: 'dependency',
      label: 'Start → Finish',
      appliesTo: ['gantt'],
      ontology: {
        predicate: 'depends_on',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { dependencyType: 'start_to_finish' },
      },
      defaultStyle: { arrowhead: 'triangle', strokeDasharray: '8 2' },
    },
    {
      id: 'milestone_gate',
      categoryId: 'dependency',
      label: 'Milestone Gate',
      appliesTo: ['gantt'],
      ontology: {
        predicate: 'milestone_gate',
        status: 'profile_only',
        facets: ['lifecycle', 'dependency'],
      },
      defaultStyle: { arrowhead: 'diamond' },
    },

    // Containment
    {
      id: 'section_contains',
      categoryId: 'containment',
      label: 'Section Contains',
      appliesTo: ['gantt', 'scorecard'],
      ontology: {
        predicate: 'contains',
        status: 'profile_only',
      },
      defaultStyle: { arrowhead: 'none' },
    },

    // Evaluation (Scorecard)
    {
      id: 'scored_against',
      categoryId: 'evaluation',
      label: 'Scored Against',
      appliesTo: ['scorecard'],
      ontology: {
        predicate: 'scored_against',
        status: 'profile_only',
        qualifierKeys: ['score', 'weight', 'basis'],
      },
      defaultStyle: { arrowhead: 'open' },
    },
    {
      id: 'outranks',
      categoryId: 'evaluation',
      label: 'Outranks',
      appliesTo: ['scorecard'],
      ontology: {
        predicate: 'outranks',
        status: 'profile_only',
        qualifierKeys: ['score', 'basis'],
      },
      defaultStyle: { arrowhead: 'triangle' },
    },

    // Causal (Fishbone — Epic SP-C wires the layout, the relationship
    // subtypes already exist so ingestion and cross-linking can emit them.)
    {
      id: 'causes',
      categoryId: 'causal',
      label: 'Causes',
      appliesTo: ['flowchart'],
      ontology: {
        predicate: 'causes',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultStyle: { arrowhead: 'triangle' },
    },
    {
      id: 'sub_causes',
      categoryId: 'causal',
      label: 'Contributes To',
      appliesTo: ['flowchart'],
      ontology: {
        predicate: 'causes',
        status: 'profile_only',
        facets: ['dependency'],
        defaultQualifiers: { causalRole: 'contributing' },
      },
      defaultStyle: { arrowhead: 'triangle', strokeDasharray: '4 2' },
    },

    // Cross-framework (the connected planning graph — Epic SP-D surfaces these)
    {
      id: 'swot_to_risk',
      categoryId: 'cross_framework',
      label: 'Becomes Risk',
      appliesTo: ['table'],
      ontology: {
        predicate: 'becomes_risk',
        status: 'profile_only',
        facets: ['lifecycle', 'dependency'],
      },
      defaultStyle: { arrowhead: 'open', strokeDasharray: '8 2' },
    },
    {
      id: 'swot_to_initiative',
      categoryId: 'cross_framework',
      label: 'Informs Initiative',
      appliesTo: ['table', 'gantt'],
      ontology: {
        predicate: 'informs',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultStyle: { arrowhead: 'open', strokeDasharray: '8 2' },
    },
    {
      id: 'risk_to_initiative',
      categoryId: 'cross_framework',
      label: 'Mitigated By',
      appliesTo: ['table', 'gantt'],
      ontology: {
        predicate: 'mitigated_by',
        status: 'profile_only',
        facets: ['governance', 'dependency'],
      },
      defaultStyle: { arrowhead: 'open', strokeDasharray: '8 2' },
    },
    {
      id: 'initiative_to_task',
      categoryId: 'cross_framework',
      label: 'Implemented By',
      appliesTo: ['table', 'gantt'],
      ontology: {
        predicate: 'implemented_by',
        status: 'profile_only',
        facets: ['dependency'],
      },
      defaultStyle: { arrowhead: 'open', strokeDasharray: '8 2' },
    },
  ],
};
